/**
 * SearchPage Component
 *
 * Main search page that assembles all search-related components:
 * - SearchInput for query input with search type selector
 * - DictionarySelector for choosing the active dictionary
 * - ResultsPanel for displaying search results
 * - QuotaIndicator for free users to see remaining searches
 *
 * Handles search state management and API calls.
 * Supports URL parameters for initial search (q=query, type=searchType).
 *
 * @validates Requirements 1.1, 2.1, 3.1, 8.6, 8.7
 */

import { useCallback, useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SearchType, type WordEntry, type QuotaStatus } from '@spellion/shared';
import {
  SearchInput,
  ResultsPanel,
  QuotaIndicator,
  DictionarySelector,
  WordDefinitionModal,
} from '../components/search';
import { useStore } from '../store';
import { useTranslation } from '../hooks/useTranslation';

// ============================================================================
// Types
// ============================================================================

/** Search API response structure */
interface SearchApiResponse {
  success: boolean;
  data?: {
    words: WordEntry[];
    totalCount: number;
    searchTimeMs: number;
    cached?: boolean;
  };
  quota?: QuotaStatus;
  error?: string;
}

/** Verify API response structure */
interface VerifyApiResponse {
  success: boolean;
  data?: {
    word: string;
    exists: boolean;
    dictionary: string;
  };
  quota?: QuotaStatus;
  error?: string;
}

// ============================================================================
// API Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// ============================================================================
// Quota Exceeded Modal
// ============================================================================

interface QuotaExceededModalProps {
  isOpen: boolean;
  onClose: () => void;
  limit: number;
  t: (key: string) => string;
}

/**
 * Modal shown when free user exceeds their daily quota
 *
 * @validates Requirement 8.2
 */
function QuotaExceededModal({ isOpen, onClose, limit, t }: QuotaExceededModalProps) {
  if (!isOpen) return null;

  // Get the message template and replace {count} placeholder
  const message = t('search.quotaExceeded.message').replace('{count}', String(limit));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quota-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal content */}
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full p-6 animate-scale-in">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
            <AlertIcon className="w-8 h-8 text-orange-500 dark:text-orange-400" />
          </div>

          <h2
            id="quota-modal-title"
            className="text-xl font-bold text-slate-900 dark:text-white mb-2"
          >
            {t('search.quotaExceeded.title')}
          </h2>

          <p className="text-slate-600 dark:text-slate-400 mb-6">
            {message}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/subscription"
              className="
                flex-1 px-4 py-3 
                bg-primary-500 text-white font-medium 
                rounded-lg hover:bg-primary-600 
                transition-colors duration-150
                text-center
              "
            >
              {t('search.quotaExceeded.upgradePremium')}
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="
                flex-1 px-4 py-3 
                bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium 
                rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 
                transition-colors duration-150
              "
            >
              {t('search.quotaExceeded.comeBackTomorrow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SearchPage Component
// ============================================================================

export function SearchPage() {
  // Translation
  const { t } = useTranslation();
  
  // Store state
  const token = useStore((state) => state.token);
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const user = useStore((state) => state.user);
  const isPremiumFn = useStore((state) => state.isPremium);
  const isPremium = isPremiumFn();
  
  // Config state
  const freeUserDailyQuota = useStore((state) => state.freeUserDailyQuota);
  const loadPublicConfig = useStore((state) => state.loadPublicConfig);

  // Search state
  const results = useStore((state) => state.results);
  const totalCount = useStore((state) => state.totalCount);
  const isLoading = useStore((state) => state.isLoading);
  const searchTimeMs = useStore((state) => state.searchTimeMs);
  const quota = useStore((state) => state.quota);
  const searchError = useStore((state) => state.error);

  // Search actions
  const setResults = useStore((state) => state.setResults);
  const clearResults = useStore((state) => state.clearResults);
  const setSearchLoading = useStore((state) => state.setSearchLoading);
  const setQuota = useStore((state) => state.setQuota);
  const setSearchError = useStore((state) => state.setSearchError);
  const clearSearchError = useStore((state) => state.clearSearchError);

  // Load public config on mount (for non-authenticated users to see correct quota value)
  useEffect(() => {
    loadPublicConfig();
  }, [loadPublicConfig]);

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Get the free searches message and replace {count} placeholder
    const freeSearchesMessage = t('search.loginRequired.freeSearches').replace('{count}', String(freeUserDailyQuota));
    
    return (
      <div className="max-w-md mx-auto py-12 text-center">
        <div className="mb-6">
          <span className="text-6xl">🔐</span>
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-4">
          {t('search.loginRequired.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-6">
          {t('search.loginRequired.message')}<br />
          <strong className="text-primary-600 dark:text-primary-400">{freeSearchesMessage}</strong>
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/register"
            className="btn-primary px-6"
          >
            {t('home.auth.createAccount')}
          </Link>
          <Link
            to="/login"
            className="btn-outline px-6"
          >
            {t('home.auth.haveAccount')}
          </Link>
        </div>
      </div>
    );
  }


  // Dictionary state
  const activeDictionary = useStore((state) => state.activeDictionary);
  const availableDictionaries = useStore((state) => state.availableDictionaries);
  const setActiveDictionary = useStore((state) => state.setActiveDictionary);
  const setAvailableDictionaries = useStore((state) => state.setAvailableDictionaries);

  // Local state
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<{
    word: string;
    exists: boolean;
    definition?: string | null;
  } | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [initialQuery, setInitialQuery] = useState<string | null>(null);
  const [initialSearchType, setInitialSearchType] = useState<SearchType>(SearchType.ANAGRAM);
  
  // URL params for initial search
  const [searchParams] = useSearchParams();
  const hasExecutedInitialSearch = useRef(false);

  // Ensure dictionaries are loaded for premium/admin users (in case they navigate directly here)
  useEffect(() => {
    if (!isPremium || !token) return;
    
    // Skip if dictionaries are already loaded (more than just the default ODS)
    if (availableDictionaries.length > 1) return;
    
    const fetchDictionaries = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/dictionaries`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.data) {
          const activeDicts = data.data.filter((d: { isActive: boolean }) => d.isActive);
          setAvailableDictionaries(activeDicts);
        }
      } catch (error) {
        console.error('Failed to load dictionaries:', error);
      }
    };
    
    fetchDictionaries();
  }, [isPremium, token, availableDictionaries.length, setAvailableDictionaries]);

  // Load quota on mount for all authenticated users (so they see their search count)
  useEffect(() => {
    // Load quota for all authenticated users
    if (!isAuthenticated || !token) return;
    
    const fetchQuota = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/subscription/quota`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.data?.quota) {
          setQuota(data.data.quota);
        }
      } catch (error) {
        console.error('Failed to load quota:', error);
      }
    };
    
    fetchQuota();
  }, [isAuthenticated, token, setQuota]);

  /**
   * Build request headers with optional auth token
   */
  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  /**
   * Handle anagram search
   */
  const handleAnagramSearch = useCallback(
    async (query: string) => {
      const response = await fetch(`${API_BASE_URL}/search/anagram`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          letters: query,
          dictionaryId: activeDictionary?.id,
        }),
      });

      const data: SearchApiResponse = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          // Quota exceeded
          setShowQuotaModal(true);
          throw new Error(t('search.quotaExceeded.title'));
        }
        throw new Error(data.error || t('common.error'));
      }

      return data;
    },
    [getHeaders, activeDictionary?.id, t]
  );

  /**
   * Handle pattern search
   */
  const handlePatternSearch = useCallback(
    async (query: string) => {
      const response = await fetch(`${API_BASE_URL}/search/pattern`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          pattern: query,
          dictionaryId: activeDictionary?.id,
        }),
      });

      const data: SearchApiResponse = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setShowQuotaModal(true);
          throw new Error(t('search.quotaExceeded.title'));
        }
        throw new Error(data.error || t('common.error'));
      }

      return data;
    },
    [getHeaders, activeDictionary?.id, t]
  );

  /**
   * Handle word verification
   */
  const handleVerifySearch = useCallback(
    async (query: string) => {
      const params = new URLSearchParams();
      if (activeDictionary?.id) {
        params.append('dictionaryId', activeDictionary.id);
      }

      const url = `${API_BASE_URL}/search/verify/${encodeURIComponent(query)}${
        params.toString() ? `?${params}` : ''
      }`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      const data: VerifyApiResponse = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setShowQuotaModal(true);
          throw new Error(t('search.quotaExceeded.title'));
        }
        throw new Error(data.error || t('common.error'));
      }

      return data;
    },
    [getHeaders, activeDictionary?.id, t]
  );

  /**
   * Main search handler
   */
  const handleSearch = useCallback(
    async (query: string, searchType: SearchType) => {
      clearSearchError();
      setVerifyResult(null);
      setSearchLoading(true);
      setHasSearched(true);

      try {
        if (searchType === SearchType.VERIFY) {
          const data = await handleVerifySearch(query);

          if (data.success && data.data) {
            // Fetch definition in parallel if word exists
            let definition: string | null = null;
            if (data.data.exists) {
              try {
                const defResponse = await fetch(`${API_BASE_URL}/config/definition/${encodeURIComponent(query)}`);
                const defData = await defResponse.json();
                if (defData.success && defData.data?.definition) {
                  definition = defData.data.definition;
                }
              } catch {
                // Ignore definition fetch errors
              }
            }

            setVerifyResult({
              word: data.data.word,
              exists: data.data.exists,
              definition,
            });
            // Clear results for verify since we show a different UI
            clearResults();
          }

          // Update quota if provided
          if (data.quota) {
            setQuota(data.quota);
          }
        } else {
          const searchFn =
            searchType === SearchType.ANAGRAM
              ? handleAnagramSearch
              : handlePatternSearch;

          const data = await searchFn(query);

          if (data.success && data.data) {
            setResults(
              data.data.words,
              data.data.totalCount,
              data.data.searchTimeMs,
              data.data.cached ?? false
            );
          }

          // Update quota if provided
          if (data.quota) {
            setQuota(data.quota);
          }
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t('common.error');
        setSearchError(message);
      } finally {
        setSearchLoading(false);
      }
    },
    [
      handleAnagramSearch,
      handlePatternSearch,
      handleVerifySearch,
      setResults,
      clearResults,
      setSearchLoading,
      setQuota,
      setSearchError,
      clearSearchError,
      t,
    ]
  );

  /**
   * Handle word click - show definition modal
   */
  const handleWordClick = useCallback(
    (word: string) => {
      setSelectedWord(word);
      setShowDefinitionModal(true);
    },
    []
  );

  /**
   * Execute initial search from URL params
   */
  useEffect(() => {
    const queryParam = searchParams.get('q');
    const typeParam = searchParams.get('type') as SearchType | null;
    
    if (queryParam && !hasExecutedInitialSearch.current) {
      hasExecutedInitialSearch.current = true;
      setInitialQuery(queryParam);
      const searchType = typeParam && Object.values(SearchType).includes(typeParam) 
        ? typeParam 
        : SearchType.ANAGRAM;
      setInitialSearchType(searchType);
      // Execute the search
      handleSearch(queryParam, searchType);
    }
  }, [searchParams, handleSearch]);

  /**
   * Handle dictionary selection change
   * Saves the preference to the server for Premium/Admin users
   */
  const handleDictionaryChange = useCallback(
    async (dictionaryId: string) => {
      const dictionary = availableDictionaries.find((d) => d.id === dictionaryId);
      if (dictionary) {
        setActiveDictionary(dictionary);
        
        // Save preference to server for Premium/Admin users
        if (isPremium && token) {
          try {
            await fetch(`${API_BASE_URL}/dictionaries/preference`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ dictionaryId }),
            });
          } catch (error) {
            console.error('Failed to save dictionary preference:', error);
          }
        }
      }
    },
    [availableDictionaries, setActiveDictionary, isPremium, token]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="text-center space-y-2">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
          {t('search.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('search.subtitle')}
        </p>
      </div>

      {/* Main Search Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-6 space-y-6">
        {/* Dictionary Selector - Only for Premium/Admin users */}
        {isPremium && (
          <DictionarySelector
            dictionaries={availableDictionaries}
            selectedId={activeDictionary?.id || 'ods'}
            onChange={handleDictionaryChange}
            disabled={isLoading}
            isPremium={isPremium}
            userId={user?.id}
            className="max-w-xs"
          />
        )}
        
        {/* Dictionary indicator for Free users (read-only) */}
        {!isPremium && activeDictionary && (
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <span className="text-slate-500 dark:text-slate-400">📖</span>
            <span>{t('search.dictionary')} : <strong className="text-slate-900 dark:text-white">{activeDictionary.name}</strong></span>
          </div>
        )}

        {/* Search Input with Type Selector */}
        <SearchInput
          onSearch={handleSearch}
          isLoading={isLoading}
          disabled={false}
          initialQuery={initialQuery || undefined}
          initialSearchType={initialSearchType}
        />

        {/* Quota Indicator (free users only) */}
        {!isPremium && quota && (
          <QuotaIndicator
            remaining={quota.remaining}
            total={quota.limit}
            isPremium={false}
          />
        )}
      </div>

      {/* Error Display */}
      {searchError && (
        <div
          className="
            flex items-center gap-3 p-4 
            bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl
            text-red-700 dark:text-red-400
          "
          role="alert"
        >
          <ErrorIcon className="w-5 h-5 flex-shrink-0" />
          <p>{searchError}</p>
          <button
            type="button"
            onClick={clearSearchError}
            className="ml-auto p-1 hover:bg-red-100 dark:hover:bg-red-800/30 rounded transition-colors"
            aria-label={t('search.closeError')}
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Verify Result Display */}
      {verifyResult && (
        <div
          className={`
            p-6 rounded-xl border-2
            ${
              verifyResult.exists
                ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }
          `}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-4">
            <div
              className={`
                w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                ${verifyResult.exists ? 'bg-success-100 dark:bg-success-900/40' : 'bg-red-100 dark:bg-red-900/40'}
              `}
            >
              {verifyResult.exists ? (
                <CheckIcon className="w-6 h-6 text-success-600 dark:text-success-400" />
              ) : (
                <CrossIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              )}
            </div>
            <div>
              <p
                className={`
                  text-lg font-bold
                  ${verifyResult.exists ? 'text-success-700 dark:text-success-400' : 'text-red-700 dark:text-red-400'}
                `}
              >
                {verifyResult.word}
              </p>
              <p
                className={`
                  text-sm
                  ${verifyResult.exists ? 'text-success-600 dark:text-success-500' : 'text-red-600 dark:text-red-500'}
                `}
              >
                {verifyResult.exists
                  ? t('search.verify.exists')
                  : t('search.verify.notExists')}
              </p>
            </div>
          </div>
          
          {/* Definition section */}
          {verifyResult.exists && (
            <div className="mt-4 pt-4 border-t border-success-200 dark:border-success-800">
              {verifyResult.definition ? (
                <div>
                  <p className="text-xs font-medium text-success-600 dark:text-success-500 mb-1">
                    {t('search.definition.title') || 'Définition'}
                  </p>
                  <p className="text-sm text-success-700 dark:text-success-400 leading-relaxed">
                    {verifyResult.definition}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-success-500 dark:text-success-600 italic">
                  {t('search.definition.notFound')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results Panel - Hide when quota is exceeded */}
      {!verifyResult && !(quota && quota.remaining === 0 && !isPremium) && (
        <ResultsPanel
          results={hasSearched ? results : null}
          isLoading={isLoading}
          searchType={SearchType.ANAGRAM}
          onWordClick={handleWordClick}
          totalCount={totalCount}
          searchTimeMs={searchTimeMs}
        />
      )}

      {/* Quota Exceeded Modal */}
      <QuotaExceededModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        limit={quota?.limit || freeUserDailyQuota}
        t={t}
      />

      {/* Word Definition Modal */}
      <WordDefinitionModal
        word={selectedWord}
        isOpen={showDefinitionModal}
        onClose={() => {
          setShowDefinitionModal(false);
          setSelectedWord(null);
        }}
      />
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function CrossIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

export default SearchPage;
